import {
  clearAccessToken,
  fetchFreshAccessToken,
  getCachedAccessToken,
  parseStoredAccessToken,
  setCachedAccessToken,
  TOKEN_STORAGE_KEY,
} from "./client";

type Listener = (token: string | null) => void;

const listeners = new Set<Listener>();
let timer: number | undefined;
let started = false;
let inflight: Promise<string | null> | null = null;
let listenersAttached = false;

function decodeExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function clearTimer() {
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timer = undefined;
  }
}

function scheduleNext(token: string | null) {
  clearTimer();
  if (!token) return;
  const exp = decodeExp(token);
  if (!exp) return;
  const ttl = exp - Date.now();
  // Refresh at 70% of TTL, never less than 30s ahead of expiry,
  // never less than 30s from now (avoids tight loops on short-TTL tokens).
  const delay = Math.max(30_000, Math.min(ttl * 0.7, ttl - 30_000));
  timer = window.setTimeout(() => {
    void refreshAccessToken("scheduled");
  }, delay);
}

function notify(token: string | null) {
  for (const listener of listeners) listener(token);
}

export async function refreshAccessToken(
  reason: "scheduled" | "force" | "subscribe",
): Promise<string | null> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const token = await fetchFreshAccessToken();
      notify(token);
      scheduleNext(token);
      return token;
    } catch (error) {
      console.warn("[auth] token refresh failed", { reason, error });
      // Try again sooner than the normal cadence, but with a floor.
      timer = window.setTimeout(() => {
        void refreshAccessToken("scheduled");
      }, 30_000);
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function handleStorage(event: StorageEvent) {
  if (event.key !== TOKEN_STORAGE_KEY) return;
  const stored = parseStoredAccessToken(event.newValue);
  if (!stored || stored.expiresAt <= Date.now()) return;
  setCachedAccessToken(stored.token, stored.expiresAt);
  notify(stored.token);
  scheduleNext(stored.token);
}

function handleOnline() {
  void refreshAccessToken("scheduled");
}

function handleVisibility() {
  if (document.visibilityState === "visible") {
    void refreshAccessToken("scheduled");
  }
}

export function startTokenManager() {
  if (started) return;
  started = true;

  if (!listenersAttached) {
    window.addEventListener("storage", handleStorage);
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);
    listenersAttached = true;
  }

  const cached = getCachedAccessToken();
  if (cached) {
    scheduleNext(cached);
  } else {
    void refreshAccessToken("subscribe");
  }
}

export function stopTokenManager() {
  clearTimer();
  started = false;
  clearAccessToken();
  notify(null);
}

export function subscribeToToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
